<script setup lang="ts">
/**
 * A chapter's own settings.
 *
 * The alignment only appears once there is more than one column, because it
 * only does anything then: sections in a single column are all the full
 * measure, so there is nothing for them to line up against.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { GroupSection } from '@/domain/section'
import {
  GROUP_ALIGN_IDS,
  GROUP_COLUMNS,
  type GroupAlign,
  type GroupColumns,
} from '@/sections/group/layout'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: GroupSection }>()
const emit = defineEmits<{ change: [section: GroupSection] }>()

const columned = computed(() => props.section.columns > 1)

function set(changes: Partial<GroupSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.group.name')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <label class="b-switch">
      <input
        type="checkbox"
        :checked="section.showTitle"
        @change="set({ showTitle: ($event.target as HTMLInputElement).checked })"
      />
      <span class="b-switch__track" />
      <span class="switch-label">{{ t('inspector.group.showTitle') }}</span>
    </label>

    <p class="note">{{ t('inspector.group.note') }}</p>

    <InspectorField :label="t('inspector.group.columns')" :hint="t('inspector.group.columnsHint')">
      <div class="segments" role="radiogroup" :aria-label="t('inspector.group.columns')">
        <button
          v-for="count in GROUP_COLUMNS"
          :key="count"
          type="button"
          role="radio"
          class="segment"
          :class="{ 'segment--on': count === section.columns }"
          :aria-checked="count === section.columns"
          :title="t('inspector.group.perLine', { count })"
          @click="set({ columns: count as GroupColumns })"
        >
          <span class="lanes" aria-hidden="true">
            <span v-for="lane in count" :key="lane" class="lane" />
          </span>
          {{ count }}
        </button>
      </div>
    </InspectorField>

    <!-- Only once there is something to line up against. -->
    <InspectorField
      v-if="columned"
      :label="t('inspector.group.align')"
      :hint="t('inspector.group.alignHint')"
    >
      <div class="segments" role="radiogroup" :aria-label="t('inspector.group.align')">
        <button
          v-for="option in GROUP_ALIGN_IDS"
          :key="option"
          type="button"
          role="radio"
          class="segment segment--wide"
          :class="{ 'segment--on': option === section.align }"
          :aria-checked="option === section.align"
          @click="set({ align: option as GroupAlign })"
        >
          {{ t(`inspector.group.alignments.${option}`) }}
        </button>
      </div>
    </InspectorField>

    <p class="count">{{ t('inspector.group.count', { count: section.children.length }) }}</p>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.switch-label {
  font-size: var(--text-base);
}

.note {
  margin: calc(var(--sp-6) * -1 + var(--sp-2)) 0 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

.count {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-sm);
}

.segments {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: 1;
  min-width: 0;
  padding: var(--sp-3) var(--sp-2);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.segment--wide {
  padding: 0 var(--sp-3);
  height: 26px;
  justify-content: center;
  white-space: nowrap;
}

/* The shape of the choice, drawn rather than described: "2" says less about
   what happens to the page than two lanes side by side do. */
.lanes {
  display: flex;
  gap: 2px;
  width: 100%;
  max-width: 34px;
  height: 12px;
}

.lane {
  flex: 1;
  border-radius: 1.5px;
  background: currentcolor;
  opacity: 0.4;
}

.segment--on .lane {
  opacity: 0.85;
}
</style>
